import { useEffect, useState } from 'react';
import { FREE_AVATARS, SHOP_ITEM_MAP, type AvatarConfig } from '@flip7/shared';
import { useAuth } from '../contexts/AuthContext';
import { usePushNotifications } from '../hooks/usePushNotifications';
import { Spinner } from '../components/Spinner';
import { Avatar } from '../components/Avatar';
import { AvatarEditorScreen } from './AvatarEditorScreen';

const API_BASE = import.meta.env.VITE_API_URL || '';

interface ProfileData {
  id: string;
  name: string;
  activeTitle: string | null;
  avatarConfig: AvatarConfig | null;
  coins: number;
  inventory: string[];
  stats: {
    gamesPlayed: number;
    gamesWon: number;
    totalBusts: number;
    totalFlip7s: number;
    highestScore: number;
  };
  unlockedTitles: string[];
}

interface ProfileScreenProps {
  onBack: () => void;
}

export function ProfileScreen({ onBack }: ProfileScreenProps) {
  const { accessToken, user, updateTitle, refreshUser } = useAuth();
  const push = usePushNotifications();
  const [profile, setProfile] = useState<ProfileData | null>(null);
  const [loading, setLoading] = useState(true);
  const [showAvatarEditor, setShowAvatarEditor] = useState(false);

  useEffect(() => {
    if (!accessToken) return;
    fetch(`${API_BASE}/api/users/me`, {
      headers: { Authorization: `Bearer ${accessToken}` },
    })
      .then(r => r.ok ? r.json() : null)
      .then(data => { if (data) setProfile(data); })
      .finally(() => setLoading(false));
  }, [accessToken]);

  if (loading) {
    return (
      <div className="hero-panel">
        <div className="screen screen-center">
          <Spinner small />
        </div>
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="hero-panel">
        <div className="screen screen-center" style={{ gap: 'var(--space-md)' }}>
          <p className="text-center-muted">Could not load profile.</p>
          <button className="btn btn-secondary" onClick={onBack}>Go Back</button>
        </div>
      </div>
    );
  }

  const winRate = profile.stats.gamesPlayed > 0
    ? Math.round((profile.stats.gamesWon / profile.stats.gamesPlayed) * 100)
    : 0;

  const saveAvatarConfig = async (config: AvatarConfig) => {
    if (!accessToken) return;
    await fetch(`${API_BASE}/api/users/me/avatar`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${accessToken}` },
      body: JSON.stringify({ avatarConfig: config }),
    });
    setProfile(prev => prev ? { ...prev, avatarConfig: config } : prev);
    setShowAvatarEditor(false);
    refreshUser();
  };

  if (showAvatarEditor) {
    return (
      <AvatarEditorScreen
        initialConfig={profile.avatarConfig}
        onSave={saveAvatarConfig}
        onCancel={() => setShowAvatarEditor(false)}
      />
    );
  }

  const equipAvatar = async (itemKey: string | null) => {
    if (!accessToken) return;
    await fetch(`${API_BASE}/api/shop/equip`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${accessToken}` },
      body: JSON.stringify({ itemKey, type: 'avatar' }),
    });
    refreshUser();
  };

  const equipItem = async (itemKey: string | null, type: 'theme' | 'soundpack') => {
    if (!accessToken) return;
    await fetch(`${API_BASE}/api/shop/equip`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${accessToken}` },
      body: JSON.stringify({ itemKey, type }),
    });
    refreshUser();
  };

  return (
    <div className="hero-panel">
      <div className="screen screen--padded-top content-narrow" style={{ padding: 'var(--space-xl) var(--space-lg)' }}>
        <div className="flex-col" style={{ gap: 'var(--space-xl)' }}>
          {/* Header */}
          <div className="page-header">
            <button onClick={onBack} className="back-btn">&larr;</button>
            <h1 className="section-header" style={{ flex: 1 }}>Profile</h1>
          </div>

          {/* Player info */}
          <div className="text-center">
            <p className="section-header" style={{ fontSize: '1.4rem' }}>{profile.name}</p>
            {user?.activeTitle && (
              <p className="profile__title">{user.activeTitle}</p>
            )}
            <div className="coin-badge" style={{ marginTop: '4px' }}>{profile.coins} coins</div>
          </div>

          {/* Pixel Avatar */}
          <div className="ornate-border ornate-border--compact">
            <p className="label" style={{ marginBottom: '8px' }}>Pixel Avatar</p>
            <div className="flex-row gap-md" style={{ alignItems: 'center' }}>
              <Avatar config={profile.avatarConfig} emoji={user?.activeAvatar} size={64} />
              <button className="btn btn-secondary btn-small" onClick={() => setShowAvatarEditor(true)}>
                {profile.avatarConfig ? 'Edit' : 'Create'} Avatar
              </button>
            </div>
          </div>

          {/* Emoji Avatar picker */}
          <div className="ornate-border ornate-border--compact">
            <p className="label" style={{ marginBottom: '8px' }}>Emoji Avatar</p>
            <div className="picker-grid">
              <button
                onClick={() => equipAvatar(null)}
                className={`picker-item${!user?.activeAvatar ? ' picker-item--active-maroon' : ''}`}
                style={{ fontSize: '0.6rem', fontWeight: 700, color: 'var(--text-muted)' }}
              >
                None
              </button>
              {(FREE_AVATARS as readonly string[]).map(emoji => (
                <button
                  key={emoji}
                  onClick={() => equipAvatar(emoji)}
                  className={`picker-item${user?.activeAvatar === emoji ? ' picker-item--active' : ''}`}
                >
                  {emoji}
                </button>
              ))}
              {profile.inventory
                .filter(key => SHOP_ITEM_MAP[key]?.type === 'avatar')
                .map(key => {
                  const item = SHOP_ITEM_MAP[key];
                  return (
                    <button
                      key={key}
                      onClick={() => equipAvatar(key)}
                      className={`picker-item${user?.activeAvatar === item.icon ? ' picker-item--active' : ''}`}
                    >
                      {item.icon}
                    </button>
                  );
                })}
            </div>
          </div>

          {/* Push Notifications */}
          <div className="ornate-border ornate-border--compact">
            <div className="flex-between" style={{ alignItems: 'center' }}>
              <div>
                <p className="label" style={{ margin: 0 }}>Notifications</p>
                <p className="profile__notification-hint">
                  {push.state === 'unsupported'
                    ? 'Not supported in this browser'
                    : push.state === 'denied'
                      ? 'Blocked in browser settings'
                      : push.state === 'subscribed'
                        ? 'You\'ll be notified of game invites'
                        : 'Get notified when friends invite you'}
                </p>
              </div>
              <button
                onClick={push.state === 'subscribed' ? push.unsubscribe : push.subscribe}
                disabled={push.state === 'unsupported' || push.state === 'denied' || push.state === 'loading'}
                className={`profile__push-btn${push.state === 'subscribed' ? ' profile__push-btn--active' : ''}`}
              >
                {push.state === 'subscribed' ? 'Disable' : push.state === 'unsupported' ? 'N/A' : push.state === 'denied' ? 'Blocked' : 'Enable'}
              </button>
            </div>
          </div>

          {/* Stats */}
          <div className="ornate-border ornate-border--compact">
            <p className="label" style={{ marginBottom: '8px' }}>Stats</p>
            <div className="profile__stats-grid">
              {[
                { label: 'Games', value: profile.stats.gamesPlayed },
                { label: 'Wins', value: `${profile.stats.gamesWon} (${winRate}%)` },
                { label: 'Busts', value: profile.stats.totalBusts },
                { label: 'Flip 7s', value: profile.stats.totalFlip7s },
                { label: 'Best Score', value: profile.stats.highestScore },
              ].map(s => (
                <div key={s.label} className="stat-row">
                  <span className="stat-row__label">{s.label}</span>
                  <span className="stat-row__value">{s.value}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Title selector */}
          {(() => {
            const purchasedTitles = profile.inventory
              .filter(key => SHOP_ITEM_MAP[key]?.type === 'title')
              .map(key => SHOP_ITEM_MAP[key].titleText!)
              .filter(Boolean);
            const allTitles = [...new Set([...profile.unlockedTitles, ...purchasedTitles])];
            if (allTitles.length === 0) return null;
            return (
              <div className="ornate-border ornate-border--compact">
                <p className="label" style={{ marginBottom: '8px' }}>Active Title</p>
                <div className="picker-grid">
                  <button
                    onClick={() => updateTitle(null)}
                    className={`picker-item picker-item--pill${!user?.activeTitle ? ' picker-item--active-maroon' : ''}`}
                  >
                    None
                  </button>
                  {allTitles.map(title => (
                    <button
                      key={title}
                      onClick={() => updateTitle(title)}
                      className={`picker-item picker-item--pill${user?.activeTitle === title ? ' picker-item--active' : ''}`}
                    >
                      {title}
                    </button>
                  ))}
                </div>
              </div>
            );
          })()}

          {/* Theme selector */}
          {profile.inventory.some(key => SHOP_ITEM_MAP[key]?.type === 'theme') && (
            <div className="ornate-border ornate-border--compact">
              <p className="label" style={{ marginBottom: '8px' }}>Theme</p>
              <div className="picker-grid">
                <button
                  onClick={() => equipItem(null, 'theme')}
                  className={`picker-item picker-item--pill${!user?.activeTheme ? ' picker-item--active-maroon' : ''}`}
                >
                  Default
                </button>
                {profile.inventory
                  .filter(key => SHOP_ITEM_MAP[key]?.type === 'theme')
                  .map(key => {
                    const item = SHOP_ITEM_MAP[key];
                    return (
                      <button
                        key={key}
                        onClick={() => equipItem(key, 'theme')}
                        className={`picker-item picker-item--pill${user?.activeTheme === key ? ' picker-item--active' : ''}`}
                      >
                        {item.icon} {item.name}
                      </button>
                    );
                  })}
              </div>
            </div>
          )}

          {/* Soundpack selector */}
          {profile.inventory.some(key => SHOP_ITEM_MAP[key]?.type === 'soundpack') && (
            <div className="ornate-border ornate-border--compact">
              <p className="label" style={{ marginBottom: '8px' }}>Sound Pack</p>
              <div className="picker-grid">
                <button
                  onClick={() => equipItem(null, 'soundpack')}
                  className={`picker-item picker-item--pill${!user?.activeSoundpack ? ' picker-item--active-maroon' : ''}`}
                >
                  Default
                </button>
                {profile.inventory
                  .filter(key => SHOP_ITEM_MAP[key]?.type === 'soundpack')
                  .map(key => {
                    const item = SHOP_ITEM_MAP[key];
                    return (
                      <button
                        key={key}
                        onClick={() => equipItem(key, 'soundpack')}
                        className={`picker-item picker-item--pill${user?.activeSoundpack === key ? ' picker-item--active' : ''}`}
                      >
                        {item.icon} {item.name}
                      </button>
                    );
                  })}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
